/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { useTranslation } from 'react-i18next';
import { Anchor, BodyText, Heading, Size } from '@vaisala/rockhopper-components';
import { ModalInstance, ModalProps } from './Modals';

export const HelpModal = (props: ModalProps) => {
  const { t } = useTranslation();
  return (
    <ModalInstance modalId="modal.help" {...props} title={t('ui.help')} size={Size.L}>
      <Heading level={4}>Calculating with Vaisala Humidity Calculator</Heading>
      <BodyText>
        Vaisala Humidity Calculator provides an easy way to convert between humidity parameters. You can also use it to calculate the effect of
        changing ambient conditions on humidity.
      </BodyText>

      <Heading level={4}>Basic and Advanced mode</Heading>
      <BodyText>
        <b>Basic mode</b> allows you to calculate common humidity parameters in different temperatures. The calculations assume that the pressure is
        at sea level and the carrier gas is air. The results are automatically calculated based on the ambient conditions and one known humidity
        parameter. To convert between two humidity parameters, adjust the ambient conditions or the known parameter.
      </BodyText>
      <BodyText>
        <b>Advanced mode</b> adds more environmental and humidity parameters. You can also enter the measurement tolerance of the known humidity
        parameter, which is then converted to tolerance of the calculated parameters. This feature is very useful for converting measurement accuracy
        specifications between humidity parameters.
      </BodyText>

      <Heading level={4}>Calculation accuracy</Heading>
      <BodyText>
        The calculation models are based on commonly available humidity theories. Additional information about the specific calculation models used in
        this application can be found from this document:&nbsp;
        <Anchor
          to="https://www.vaisala.com/system/files/documents/Humidity-conversion-formulas-Technical-eBook-B210973EN.pdf"
          openInNewWindow={true}
        >
          {t('ui.humidity.formulas')}
        </Anchor>
      </BodyText>
      <BodyText>
        Reliability of these calculations are limited to specific temperature and pressure ranges. The selected gas type also affects calculation
        accuracy. If air or nitrogen (N<sub>2</sub>) are selected, an enhancement factor is used, which enables accurate calculations in pressures up
        to 2 MPa. In the case of other gases, this factor is not used, and accuracy is limited to lower pressures.
      </BodyText>
      <Heading level={4}>Warning messages due to impossible environmental conditions</Heading>
      <BodyText>
        This calculator allows total freedom when entering values in the input fields. This may naturally lead to situations that are not possible in
        the real world. For example, the calculation result may exceed relative humidity of 100 %rh. In such cases, the readings no longer represent a
        real environment, and therefore all values will be shown in red, accompanied with a red message box. You can resolve this situation by
        modifying the environmental conditions. For example, you can increase the ambient temperature or decrease the humidity value.
      </BodyText>
      <BodyText>A warning message is also displayed for environmental conditions that may cause condensation or ice formation.</BodyText>
      <BodyText>
        We are constantly looking for ways to improve the calculator. Any improvement ideas are very welcome. Please email your comments, suggestions
        or feedback:&nbsp;
        <Anchor to="mailto:humiditycalculator@vaisala.com" openInNewWindow={true}>
          humiditycalculator@vaisala.com
        </Anchor>
      </BodyText>
    </ModalInstance>
  );
};
